import React from "react";
import styled from "@emotion/styled/macro";
import { Typography, tokens } from "@sunrun/experience-ui-components";
import { DuplicateValidationWarningBanner } from "../../molecules/DuplicateValidationWarningBanner";
import { MissingContactInfoBanner } from "../../molecules/MissingContactInfoBanner";
import { StepBody, StepHeader } from "./SharedStyles";
import { isMissingContactValue } from "utils/contactValidation";

type ContactInfoStepProps = {
  name: string;
  phone: string;
  email: string;
  onUpdate: () => void;
  validationStatus?: string;
  isDuplicatePhone: boolean;
  isDuplicateEmail: boolean;
};

const displayContactValue = (value: string) =>
  isMissingContactValue(value) ? "-" : value;

const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  name,
  phone,
  email,
  onUpdate,
  validationStatus,
  isDuplicatePhone,
  isDuplicateEmail,
}) => {
  const displayPhone = displayContactValue(phone);
  const displayEmail = displayContactValue(email);

  return (
    <StepBody>
      <StepHeader
        title="Confirm phone number and email address"
        description="Contact information cannot be changed after agreement signature."
        onUpdate={onUpdate}
      />

      <DuplicateValidationWarningBanner
        validationStatus={validationStatus}
        actionLabel="Edit"
        onViewDetails={onUpdate}
        viewDetailsTestId="confirm-names-duplicate-validation-edit"
      />

      <MissingContactInfoBanner
        phone={phone}
        email={email}
        onEdit={onUpdate}
        editTestId="confirm-names-missing-contact-info-edit"
      />

      <ContactInfoDisplay data-testid="confirm-names-contact-info">
        <ContactNameRow>
          <Typography color={tokens.HEROBLUE_60} size={tokens.FONT_SIZE_0}>
            {name}
          </Typography>
        </ContactNameRow>
        <ContactInfoColumns>
          <ContactInfoColumn>
            <Typography
              color={tokens.BRAND_HERO_BLUE}
              size={tokens.FONT_SIZE_3}
              fontWeight="regular"
            >
              Phone number
            </Typography>
            <ContactValue
              color={
                isDuplicatePhone ? tokens.ERROR_50 : tokens.BRAND_HERO_BLUE
              }
              size={tokens.FONT_SIZE_5}
              testId="confirm-names-contact-phone"
            >
              {displayPhone}
            </ContactValue>
          </ContactInfoColumn>
          <ContactInfoColumn>
            <Typography
              color={tokens.BRAND_HERO_BLUE}
              size={tokens.FONT_SIZE_3}
              fontWeight="regular"
            >
              Email
            </Typography>
            <ContactValue
              color={
                isDuplicateEmail ? tokens.ERROR_50 : tokens.BRAND_HERO_BLUE
              }
              size={tokens.FONT_SIZE_5}
              testId="confirm-names-contact-email"
            >
              {displayEmail}
            </ContactValue>
          </ContactInfoColumn>
        </ContactInfoColumns>
      </ContactInfoDisplay>
    </StepBody>
  );
};

const ContactInfoDisplay = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContactNameRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 12px 8px 0;
  border-bottom: 1px solid rgba(28, 28, 28, 0.2);
`;

const ContactInfoColumns = styled.div`
  display: flex;
  width: 100%;
  background-color: ${tokens.BRAND_OFF_GREY};
`;

const ContactInfoColumn = styled.div`
  flex: 0 1 fit-content;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 24px;
`;

const ContactValue = styled(Typography)`
  font-weight: 600;
  word-break: break-word;
`;

export { ContactInfoStep };
export type { ContactInfoStepProps };
